/**
 * NarratorCard — Compact AI summary widget for embedding in pages.
 * Fetches a one-shot contextual summary from the AI Narrator API.
 */
import React from 'react';
import {
  Card,
  Body1,
  Caption1,
  tokens,
  makeStyles,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import { SparkleRegular, WarningRegular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { narratorApi } from '../../services/api';
import type { ContextType } from '../../types';

interface NarratorCardProps {
  contextType: ContextType;
  contextData?: Record<string, unknown>;
}

const AI_PURPLE = '#8852E0';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM + ' ' + tokens.spacingHorizontalL,
    borderLeft: `3px solid ${AI_PURPLE}`,
  },
  icon: {
    color: AI_PURPLE,
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '2px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
    minWidth: 0,
  },
  label: {
    color: AI_PURPLE,
    fontWeight: 600,
    fontSize: tokens.fontSizeBase200,
  },
  summary: {
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
});

export const NarratorCard: React.FC<NarratorCardProps> = ({ contextType, contextData }) => {
  const styles = useStyles();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['narrator-summary', contextType, contextData],
    queryFn: () => narratorApi.getSummary(contextType, contextData),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const hasAccountIssue = data?.error === 'ai_account_attention_required' || data?.error === 'ai_authentication_error';

  // Show configuration hint when AI is unavailable
  if (isError) {
    return (
      <Card className={styles.card}>
        <SparkleRegular className={styles.icon} />
        <div className={styles.content}>
          <Caption1 className={styles.label}>AI Insight</Caption1>
          <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
            AI Narrator is temporarily unavailable.
          </Body1>
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <SparkleRegular className={styles.icon} />
      <div className={styles.content}>
        <Caption1 className={styles.label}>AI Insight</Caption1>
        {isLoading ? (
          <div className={styles.skeleton}>
            <Skeleton>
              <SkeletonItem style={{ width: '100%', height: '16px' }} />
            </Skeleton>
            <Skeleton>
              <SkeletonItem style={{ width: '75%', height: '16px' }} />
            </Skeleton>
          </div>
        ) : (
          <>
            <Body1 className={styles.summary}>{data?.summary}</Body1>
            {hasAccountIssue && (
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, marginTop: tokens.spacingVerticalXS }}>
                <WarningRegular style={{ color: tokens.colorPaletteYellowForeground1, fontSize: '14px' }} />
                <Caption1 style={{ color: tokens.colorPaletteYellowForeground1 }}>
                  AI Narrator service account requires attention. Contact your administrator.
                </Caption1>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};
